import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  Calendar, 
  FileText, 
  Star, 
  Users, 
  BarChart3, 
  Activity, 
  Zap, 
  RefreshCw, 
  ArrowUpRight, 
  ShieldCheck 
} from 'lucide-react';
import { getAdminOverview } from '../../lib/adminApi';

export default function AdminOverview({ onNavigateTab }) {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await getAdminOverview();
      setOverview(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const counts = overview?.counts || {};
  const recent = overview?.recentActivity || [];

  const cards = [
    {
      title: 'Strategy Session Bookings',
      count: counts.bookings || 0,
      icon: Calendar,
      col: 'bookings',
      color: 'from-amber-500/20 to-amber-600/5 border-amber-500/30 text-amber-400'
    },
    {
      title: 'Lead Magnet Downloads',
      count: counts.lead_magnets || 0,
      icon: FileText,
      col: 'lead_magnets',
      color: 'from-blue-500/20 to-blue-600/5 border-blue-500/30 text-blue-400'
    },
    {
      title: 'Client Testimonials',
      count: counts.testimonials || 0,
      icon: Star,
      col: 'testimonials',
      color: 'from-yellow-500/20 to-yellow-600/5 border-yellow-500/30 text-yellow-400'
    },
    {
      title: 'Contact Entries',
      count: counts.contacts || 0,
      icon: Users,
      col: 'contacts',
      color: 'from-emerald-500/20 to-emerald-600/5 border-emerald-500/30 text-emerald-400'
    },
    {
      title: 'FNA Financial Reports',
      count: counts.fna_reports || 0,
      icon: BarChart3,
      col: 'fna_reports',
      color: 'from-purple-500/20 to-purple-600/5 border-purple-500/30 text-purple-400'
    },
    {
      title: 'Agent Tool Executions',
      count: counts.tool_usage || 0,
      icon: Activity,
      col: 'tool_usage',
      color: 'from-indigo-500/20 to-indigo-600/5 border-indigo-500/30 text-indigo-400'
    }
  ];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold font-playfair text-white">System Dashboard</h2>
          <p className="text-slate-400 text-sm">Real-time overview of Firestore collections, client bookings & agent activity.</p>
        </div>

        <Button
          type="button"
          onClick={loadData}
          disabled={loading}
          className="bg-[#162236] hover:bg-[#1f2e48] text-slate-200 border border-[#C9A84C]/30 px-4 py-2 rounded-xl text-sm font-semibold flex items-center gap-2"
        >
          <RefreshCw className={`w-4 h-4 text-[#C9A84C] ${loading ? 'animate-spin' : ''}`} />
          Refresh Metrics
        </Button>
      </div>

      {/* Metrics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {cards.map((card, i) => {
          const IconComp = card.icon;
          return (
            <Card 
              key={i} 
              className={`bg-gradient-to-br ${card.color} border bg-[#0D1B2A]/90 rounded-2xl shadow-xl transition-all duration-300 hover:scale-[1.02] cursor-pointer`}
              onClick={() => onNavigateTab && onNavigateTab('explorer', card.col)}
            >
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-slate-300">
                  {card.title}
                </CardTitle>
                <div className="p-2 rounded-xl bg-[#162236] border border-[#C9A84C]/20">
                  <IconComp className="w-5 h-5" />
                </div>
              </CardHeader>
              <CardContent className="space-y-1">
                <div className="text-3xl font-bold text-white font-playfair">
                  {loading ? '...' : card.count}
                </div>
                <div className="flex items-center gap-1 text-xs text-slate-400 hover:text-[#E8D4A0]">
                  <span>Explore collection</span>
                  <ArrowUpRight className="w-3.5 h-3.5" />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Quick Action Banner */}
      <div className="bg-[#162236]/90 border border-[#C9A84C]/40 rounded-2xl p-6 flex flex-col md:flex-row justify-between items-center gap-6 shadow-xl backdrop-blur-xl">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-[#C9A84C]/20 border border-[#C9A84C] flex items-center justify-center text-[#C9A84C]">
            <Zap className="w-6 h-6" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-white font-playfair">Cloud Function Triggers Available</h3>
            <p className="text-slate-300 text-xs sm:text-sm">Re-send lead magnet PDFs, booking calendar invitations, or dispatch custom transactional emails directly to any client.</p>
          </div>
        </div>

        <Button
          onClick={() => onNavigateTab && onNavigateTab('triggers')}
          className="bg-gradient-to-r from-[#C9A84C] to-[#8C6D23] hover:brightness-110 text-[#070E1B] font-bold px-6 py-3 rounded-xl shadow-lg flex items-center gap-2 whitespace-nowrap"
        >
          <Zap className="w-4 h-4" />
          Open Trigger Center
        </Button>
      </div>

      {/* Recent Activity Section */}
      <Card className="bg-[#0D1B2A]/90 border border-[#C9A84C]/30 rounded-2xl shadow-xl">
        <CardHeader>
          <CardTitle className="text-lg font-bold font-playfair text-white flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-[#C9A84C]" />
            Recent Record Activity Across Collections
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-8 text-center text-slate-400 text-sm">Loading activity feed...</div>
          ) : recent.length === 0 ? (
            <div className="py-8 text-center text-slate-400 text-sm">No activity recorded yet.</div>
          ) : (
            <div className="divide-y divide-[#1e293b]">
              {recent.map((item, index) => (
                <div key={index} className="py-3 flex items-center justify-between hover:bg-[#162236]/50 px-3 rounded-xl transition-colors">
                  <div className="space-y-0.5">
                    <p className="text-sm font-semibold text-white">{item.title}</p>
                    <p className="text-xs text-slate-400">Collection: <span className="text-[#E8D4A0] font-mono">{item.collection}</span> &bull; ID: {item.id}</p>
                  </div>
                  <span className="text-xs text-slate-500 font-mono">
                    {new Date(item.createdAt).toLocaleDateString()}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
